import dynamic from "next/dynamic";
import type { ParagraphData } from "@/lib/api/cms-catalog";
import {
  PARAGRAPH_TEXT_LAYOUT,
  PARAGRAPH_TEXT_IMAGES_LEFT_LAYOUT,
  PARAGRAPH_TEXT_IMAGES_RIGHT_LAYOUT,
  PARAGRAPH_TEXT_IMAGES_TOP_LAYOUT,
  PARAGRAPH_TEXT_IMAGES_BOT_LAYOUT,
  PARAGRAPH_TWO_COLS_TEXT_LAYOUT,
  PARAGRAPH_TWO_COLS_TEXT_VIDEO_LAYOUT,
  PARAGRAPH_TWO_COLS_TABLE_TEXT_LAYOUT,
  PARAGRAPH_TWO_COLS_TEXT_GALLERY_LAYOUT,
  PARAGRAPH_PREZENTATION_LAYOUT,
  PARAGRAPH_BIG_PHOTO_LAYOUT,
  PARAGRAPH_THREE_COLS_TEXT_LAYOUT,
  PARAGRAPH_THREE_COLS_TEXT_TEXT_PHOTO_LAYOUT,
  PARAGRAPH_THREE_COLS_TEXT_PHOTO_TEXT_LAYOUT,
  PARAGRAPH_THREE_COLS_PHOTO_TEXT_TEXT_LAYOUT,
  PARAGRAPH_THREE_COLS_BIG_VIDEO_LAYOUT,
  PARAGRAPH_TWO_BOXES_LAYOUT,
  PARAGRAPH_THREE_BOXES_LAYOUT,
  PARAGRAPH_CONTACT_FORM_LAYOUT,
  PARAGRAPH_MAPS_LAYOUT,
  PARAGRAPH_TWO_COLS_TEXT_PREZENTATION_LAYOUT,
  PARAGRAPH_BLOG_POSTS_LAYOUT,
} from "@/lib/cms/paragraphLayouts";
import type { ParagraphTranslations } from "./Paragraph.i18n";
import { ParagraphText } from "./layouts/ParagraphText";
import { ParagraphTextImages } from "./layouts/ParagraphTextImages";
import { ParagraphTwoColsText } from "./layouts/ParagraphTwoColsText";
import { ParagraphTwoColsTextVideo } from "./layouts/ParagraphTwoColsTextVideo";
import { ParagraphTwoColsTableText } from "./layouts/ParagraphTwoColsTableText";
import { ParagraphBigPhoto } from "./layouts/ParagraphBigPhoto";
import { ParagraphThreeColsText } from "./layouts/ParagraphThreeColsText";
import { ParagraphThreeColsTextImages } from "./layouts/ParagraphThreeColsTextImages";
import { ParagraphBigVideo } from "./layouts/ParagraphBigVideo";
import { ParagraphTwoBoxes } from "./layouts/ParagraphTwoBoxes";
import { ParagraphThreeBoxes } from "./layouts/ParagraphThreeBoxes";
import { ParagraphContactForm } from "./layouts/ParagraphContactForm";
import { ParagraphMaps } from "./layouts/ParagraphMaps";
import { ParagraphBlogPosts } from "./layouts/ParagraphBlogPosts";
import styles from "./Paragraph.module.css";

/**
 * **Dynamic import dla layoutów ze Swiper'em** — ParagraphPresentation (layout 7)
 * i ParagraphTwoColsTextPresentation (layout 24) importują `swiper/react` +
 * 3 moduły + `yet-another-react-lightbox` (~50-80 KB JS gzipped + 3 osobne CSS
 * chunki). Powodowało:
 *   - "Reduce unused JavaScript" — kod ładowany na każdej stronie nawet gdy
 *     żaden akapit nie ma layoutu 7/24
 *   - "Forced reflow" — Swiper mierzy container przez offsetWidth/offsetHeight
 *     przy mount, 170 ms blocking
 *
 * `dynamic()` w server component (Paragraph dispatcher) split'uje chunk —
 * Swiper bundle ładuje się TYLKO gdy strona faktycznie ma taki layout, i
 * to po main bundle (poza krytyczną ścieżką LCP/TBT). `ssr: true` (default)
 * — SSR markup z slidów/galerii zachowany (SEO content widoczny).
 *
 * Per Next.js 16 docs: `dynamic()` w server component może lazy-loadować
 * client component bez `ssr: false`. To wystarczy żeby chunk split działał.
 */
const ParagraphPresentation = dynamic(() =>
  import("./layouts/ParagraphPresentation").then((m) => ({
    default: m.ParagraphPresentation,
  })),
);
const ParagraphTwoColsTextPresentation = dynamic(() =>
  import("./layouts/ParagraphTwoColsTextPresentation").then((m) => ({
    default: m.ParagraphTwoColsTextPresentation,
  })),
);

/**
 * Paragraph — dispatcher per `layout` (discriminated union switch).
 *
 * **Exhaustive check:** TypeScript wymusi handler dla każdego variantu w
 * `ParagraphData`. Gdy backend doda nowy layout -> typ wymusi update.
 *
 * **Reusable z dowolnego komponentu** — używany przez `<ParagraphRenderer>`
 * dla listy akapitów, ale można też wywołać bezpośrednio dla pojedynczego
 * akapitu z innego komponentu CMS / okna / panelu admina.
 *
 * Nieznane layouty -> placeholder dev hint (informacyjny, nie wybucha — pozwala
 * dodać nowy layout backend-first bez awarii istniejących stron).
 */
export function Paragraph({
  paragraph,
  t,
}: {
  paragraph: ParagraphData;
  t: ParagraphTranslations;
}) {
  switch (paragraph.layout) {
    case PARAGRAPH_TEXT_LAYOUT:
      return <ParagraphText paragraph={paragraph} t={t} />;

    case PARAGRAPH_TEXT_IMAGES_LEFT_LAYOUT:
    case PARAGRAPH_TEXT_IMAGES_RIGHT_LAYOUT:
    case PARAGRAPH_TEXT_IMAGES_TOP_LAYOUT:
    case PARAGRAPH_TEXT_IMAGES_BOT_LAYOUT:
    case PARAGRAPH_TWO_COLS_TEXT_GALLERY_LAYOUT:
      return <ParagraphTextImages paragraph={paragraph} t={t} />;

    case PARAGRAPH_TWO_COLS_TEXT_LAYOUT:
      return <ParagraphTwoColsText paragraph={paragraph} t={t} />;

    case PARAGRAPH_TWO_COLS_TEXT_VIDEO_LAYOUT:
      return <ParagraphTwoColsTextVideo paragraph={paragraph} t={t} />;

    case PARAGRAPH_TWO_COLS_TABLE_TEXT_LAYOUT:
      return <ParagraphTwoColsTableText paragraph={paragraph} t={t} />;

    case PARAGRAPH_PREZENTATION_LAYOUT:
      return <ParagraphPresentation paragraph={paragraph} t={t} />;

    case PARAGRAPH_BIG_PHOTO_LAYOUT:
      return <ParagraphBigPhoto paragraph={paragraph} t={t} />;

    case PARAGRAPH_THREE_COLS_TEXT_LAYOUT:
      return <ParagraphThreeColsText paragraph={paragraph} t={t} />;

    case PARAGRAPH_THREE_COLS_TEXT_TEXT_PHOTO_LAYOUT:
    case PARAGRAPH_THREE_COLS_TEXT_PHOTO_TEXT_LAYOUT:
    case PARAGRAPH_THREE_COLS_PHOTO_TEXT_TEXT_LAYOUT:
      return <ParagraphThreeColsTextImages paragraph={paragraph} t={t} />;

    case PARAGRAPH_THREE_COLS_BIG_VIDEO_LAYOUT:
      return <ParagraphBigVideo paragraph={paragraph} t={t} />;

    case PARAGRAPH_TWO_BOXES_LAYOUT:
      return <ParagraphTwoBoxes paragraph={paragraph} t={t} />;

    case PARAGRAPH_THREE_BOXES_LAYOUT:
      return <ParagraphThreeBoxes paragraph={paragraph} t={t} />;

    case PARAGRAPH_CONTACT_FORM_LAYOUT:
      return <ParagraphContactForm paragraph={paragraph} t={t} />;

    case PARAGRAPH_MAPS_LAYOUT:
      return <ParagraphMaps paragraph={paragraph} t={t} />;

    case PARAGRAPH_TWO_COLS_TEXT_PREZENTATION_LAYOUT:
      return <ParagraphTwoColsTextPresentation paragraph={paragraph} t={t} />;

    case PARAGRAPH_BLOG_POSTS_LAYOUT:
      return <ParagraphBlogPosts paragraph={paragraph} t={t} />;

    default:
      return <UnsupportedParagraph paragraph={paragraph} t={t} />;
  }
}

/**
 * UnsupportedParagraph — placeholder dla layoutów których renderer nie zna.
 *
 * Renderowany gdy backend zwrócił `layout` spoza znanych enumów (np. nowy
 * layout dodany w PHP `define.php` ale jeszcze nie odzwierciedlony w React-CMS).
 * Pokazuje dev hint zamiast wybuchać.
 */
function UnsupportedParagraph({
  paragraph,
  t,
}: {
  paragraph: { layout?: number | string };
  t: ParagraphTranslations;
}) {
  const layoutNum =
    typeof paragraph.layout === "number"
      ? paragraph.layout
      : parseInt(String(paragraph.layout ?? "0"), 10) || 0;
  return (
    <section className={styles.paragraph}>
      <div className={styles.paragraphUnsupported}>
        <strong>{t.unsupportedLayout(layoutNum)}</strong> {t.unsupportedLayoutBody}
      </div>
    </section>
  );
}
